import { Controller } from "@hotwired/stimulus"

// Full-screen photo viewer.
//
// Tap an image with data-action="click->photo-lightbox#open" to open it. If the
// image also carries data-lightbox-group="X", every image on the page sharing
// that group becomes a swipeable gallery.
//
// Gestures (when not zoomed): swipe left/right = prev/next, swipe down = close,
// tap = close. Pinch zoom is contained to the photo and resets on navigate/close,
// so it can never leak into the app's own page zoom (the viewport also locks
// page zoom — see the layout's meta tag).
export default class extends Controller {
  open(event) {
    const clicked = event.currentTarget
    const group   = clicked.dataset.lightboxGroup

    const imgs = group
      ? Array.from(document.querySelectorAll(`[data-lightbox-group="${CSS.escape(group)}"]`))
      : [clicked]

    this.sources = imgs.map(i => i.src || i.dataset.src).filter(Boolean)
    this.index   = Math.max(0, imgs.indexOf(clicked))
    if (this.sources.length === 0) return

    this._build()
    this._show(this.index)
  }

  _build() {
    const overlay = document.createElement("div")
    Object.assign(overlay.style, {
      position: "fixed", inset: "0", background: "rgba(0,0,0,0.94)", zIndex: "99999",
      display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden",
      touchAction: "none", WebkitBackdropFilter: "blur(4px)", backdropFilter: "blur(4px)",
      opacity: "0", transition: "opacity 0.2s ease", padding: "20px", boxSizing: "border-box",
    })

    const photo = document.createElement("img")
    Object.assign(photo.style, {
      maxWidth: "100%", maxHeight: "100%", objectFit: "contain", borderRadius: "12px",
      boxShadow: "0 24px 80px rgba(0,0,0,0.6)", transformOrigin: "center center",
      willChange: "transform", pointerEvents: "none", WebkitUserSelect: "none", userSelect: "none",
    })

    const close = document.createElement("button")
    Object.assign(close.style, {
      position: "absolute", top: "16px", right: "16px", width: "36px", height: "36px",
      borderRadius: "50%", border: "none", background: "rgba(255,255,255,0.15)", color: "#fff",
      fontSize: "20px", lineHeight: "1", cursor: "pointer", display: "flex",
      alignItems: "center", justifyContent: "center", backdropFilter: "blur(8px)", zIndex: "1",
    })
    close.innerHTML = "×"
    close.setAttribute("aria-label", "Close")

    const counter = document.createElement("div")
    Object.assign(counter.style, {
      position: "absolute", top: "22px", left: "0", right: "0", textAlign: "center",
      color: "rgba(255,255,255,0.7)", fontSize: "13px", fontFamily: "-apple-system, BlinkMacSystemFont, sans-serif",
      pointerEvents: "none",
    })

    overlay.appendChild(photo)
    overlay.appendChild(close)
    if (this.sources.length > 1) overlay.appendChild(counter)
    document.body.appendChild(overlay)

    this.overlay = overlay
    this.photo   = photo
    this.counter = counter

    requestAnimationFrame(() => { overlay.style.opacity = "1" })
    close.addEventListener("click", (e) => { e.stopPropagation(); this._dismiss() })
    this._wireGestures()

    // Tell native to suspend the tab-swipe gesture while we're open, so horizontal
    // swipes page between photos instead of switching tabs.
    this._signalOverlay(true)
  }

  _signalOverlay(open) {
    window.webkit?.messageHandlers?.nativeOverlay?.postMessage(open)
  }

  _show(i) {
    this.index = (i + this.sources.length) % this.sources.length
    this.scale = 1; this.tx = 0; this.ty = 0
    this.photo.src = this.sources[this.index]
    this._apply()
    if (this.counter) this.counter.textContent = `${this.index + 1} / ${this.sources.length}`
  }

  _apply() {
    this.photo.style.transform = `translate(${this.tx}px, ${this.ty}px) scale(${this.scale})`
  }

  _dismiss() {
    this._signalOverlay(false)
    this.overlay.style.opacity = "0"
    setTimeout(() => this.overlay.remove(), 200)
  }

  _wireGestures() {
    const o = this.overlay
    let startX = 0, startY = 0, curX = 0, curY = 0, moved = false
    let pinchDist = 0, pinchScale = 1, panTx = 0, panTy = 0
    const dist = (t) => Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY)

    o.addEventListener("touchstart", (e) => {
      moved = false
      if (e.touches.length === 2) {
        pinchDist = dist(e.touches); pinchScale = this.scale
      } else {
        startX = curX = e.touches[0].clientX
        startY = curY = e.touches[0].clientY
        panTx = this.tx; panTy = this.ty
      }
    }, { passive: true })

    o.addEventListener("touchmove", (e) => {
      moved = true
      if (e.touches.length === 2) {
        this.scale = Math.min(Math.max(pinchScale * (dist(e.touches) / pinchDist), 1), 5)
        this._apply()
      } else {
        curX = e.touches[0].clientX
        curY = e.touches[0].clientY
        if (this.scale > 1) { this.tx = panTx + (curX - startX); this.ty = panTy + (curY - startY); this._apply() }
      }
    }, { passive: true })

    o.addEventListener("touchend", () => {
      if (this.scale > 1) return             // zoomed: gestures pan, don't navigate/close
      if (!moved) { this._dismiss(); return } // tap closes
      const dx = curX - startX, dy = curY - startY
      if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) {
        this._show(this.index + (dx < 0 ? 1 : -1))
      } else if (dy > 70 && dy > Math.abs(dx)) {
        this._dismiss()
      }
    }, { passive: true })

    // Desktop fallback
    o.addEventListener("click", (e) => { if (e.target === o && this.scale <= 1) this._dismiss() })
  }
}
